/**
 * Rental-type-aware copy for the post-booking trip-detail / handover
 * surfaces: the web trip-detail page and the mobile booking-detail screen.
 *
 * This mirrors the wording *direction* of the api-server pre-trip reminder
 * helper (`api-server/src/lib/rental-type-copy.ts`) so a chauffeured or
 * event booking reads consistently from the reminder notification all the
 * way through to the trip screen — instead of dropping back to self-drive
 * language ("pick up", "collect the car", "bring your licence") the moment
 * the renter leaves the notification.
 *
 * A booking's `rental_type` is one of:
 *   - `driving`     — self-drive; the renter collects and drives the car.
 *   - `chauffeured` — the owner/driver arrives with the car and drives it.
 *   - `event`       — the car is brought to the renter's event/display, no
 *                     driving.
 *
 * The `driving` branch reproduces the exact strings the mobile booking
 * screen emitted before this change, so self-drive bookings render
 * byte-for-byte identically. Callers that can't resolve a rental type fall
 * back to `driving` (see `normalizeTripDetailRentalType`).
 */

export const TRIP_DETAIL_RENTAL_TYPES = ["driving", "chauffeured", "event"] as const;
export type TripDetailRentalType = (typeof TRIP_DETAIL_RENTAL_TYPES)[number];

/**
 * Coerce any upstream `rental_type` value (canonical enum, legacy
 * "self_drive"/"chauffeur" spellings, smallint-as-string, null) into one of
 * the three concrete rental types, defaulting to `driving`.
 */
export function normalizeTripDetailRentalType(value: unknown): TripDetailRentalType {
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (v === "chauffeured" || v === "chauffeur" || v === "1") return "chauffeured";
    if (v === "event" || v === "events" || v === "2") return "event";
  }
  return "driving";
}

export interface TripDetailCopyInput {
  rentalType: TripDetailRentalType;
  /** True for the owner/host view, false for the renter view. */
  isOwner: boolean;
}

export interface TripDetailCopy {
  /** Subtitle on the instant-confirmation hero shown right after booking (renter). */
  justBookedInstantSubtitle: string;
  /** Status subtitle once the booking is confirmed and awaiting the trip (renter). */
  confirmedSubtitle: string;
  /** Status subtitle while the trip is underway (renter). */
  inProgressSubtitle: string;
  /**
   * One-line "what to expect" handover summary, role-aware. Empty string for
   * `driving` so callers can choose to render nothing (and leave the
   * pre-existing self-drive view untouched).
   */
  whatToExpect: string;
}

/**
 * Resolve the rental-type-aware copy for a trip-detail / handover surface.
 * `driving` reproduces the pre-existing self-drive strings exactly.
 */
export function getTripDetailCopy(input: TripDetailCopyInput): TripDetailCopy {
  const { rentalType, isOwner } = input;

  if (rentalType === "chauffeured") {
    return {
      justBookedInstantSubtitle: "The host has been notified. Your driver will arrive with the car.",
      confirmedSubtitle: "Confirmed. We'll remind you ahead of your trip.",
      inProgressSubtitle: "Trip in progress. Enjoy the ride.",
      whatToExpect: isOwner
        ? "You're chauffeuring this trip — confirm the pickup time and meeting point with your renter."
        : "Your driver will arrive with the car at the agreed time — no licence or key handover needed.",
    };
  }

  if (rentalType === "event") {
    return {
      justBookedInstantSubtitle: "The host has been notified. We'll share arrival details shortly.",
      confirmedSubtitle: "Confirmed. We'll remind you ahead of your event.",
      inProgressSubtitle: "Your event is underway. Enjoy!",
      whatToExpect: isOwner
        ? "You're bringing this car to the event — confirm the arrival time and any setup details with your renter."
        : "Your car arrives for your event at the agreed time — no driving or licence needed.",
    };
  }

  // driving (self-drive) — must reproduce the pre-change strings exactly.
  return {
    justBookedInstantSubtitle: "The host has been notified. You'll get pickup details shortly.",
    confirmedSubtitle: "Confirmed. We'll remind you ahead of pickup.",
    inProgressSubtitle: "Trip in progress. Drive safely.",
    // Empty by design: self-drive surfaces render their existing copy and
    // skip the extra handover note, so driving bookings are unchanged.
    whatToExpect: "",
  };
}
